import { createDifferentGroupingsApiAdapter } from "./differentGroupingsApiAdapter";

/**
 * UI-local seam for Different Groupings. Future route components use this
 * interface; they never read localStorage or construct a TCSD URL themselves.
 */
const disabledGateway = Object.freeze({ available: false, reason: "Different Groupings is unavailable." });

let loadDevelopmentAdapter = null;
if (process.env.NODE_ENV !== "production") {
  loadDevelopmentAdapter = () => import("./differentGroupingsLocalStorageAdapter");
}

export const isDifferentGroupingsMockEnabled = (environment = process.env) =>
  environment.NODE_ENV !== "production" &&
  environment.REACT_APP_CLASSROOM_HANDOVER_DIFFERENT_GROUPINGS_MOCK === "true";

export const loadDifferentGroupingsGateway = (options = {}) => {
  // Keep this predicate inline so CRA can erase the dynamic import from a
  // production build even when the mock flag is forcibly true.
  if (!(process.env.NODE_ENV !== "production" &&
    process.env.REACT_APP_CLASSROOM_HANDOVER_DIFFERENT_GROUPINGS_MOCK === "true")) {
    return Promise.resolve({ available: true, ...createDifferentGroupingsApiAdapter(options) });
  }

  // This development/test-only dynamic import is deliberately behind the
  // production predicate so the adapter cannot enter a production bundle.
  const loadAdapter = options.loadAdapter || loadDevelopmentAdapter;
  if (!loadAdapter) return Promise.resolve(disabledGateway);
  return loadAdapter().then(({ createDifferentGroupingsLocalStorageAdapter }) => ({
    available: true,
    ...createDifferentGroupingsLocalStorageAdapter(options),
  }));
};

const buildTcsdHandoffDestination = (
  url,
  { schoolId, academicYear, handoffCode } = {},
  allowOpaqueDevelopmentHandoff = false
) => {
  const destination = new URL(url);
  if (!["http:", "https:"].includes(destination.protocol)) {
    throw new Error("Unsupported TCSD URL protocol.");
  }
  if (destination.username || destination.password || destination.search || destination.hash) {
    throw new Error("TCSD handoff destinations must not contain credentials or task data.");
  }
  if (!handoffCode) {
    if (!allowOpaqueDevelopmentHandoff) throw new Error("A secure TCSD handoff code is required.");
    return destination.toString();
  }
  if (!schoolId || !academicYear) throw new Error("TCSD handoff scope is required.");
  destination.pathname = "/masterdashboard";
  destination.searchParams.set("source", "td");
  destination.searchParams.set("workflow", "classroom-transition");
  destination.searchParams.set("schoolId", schoolId);
  destination.searchParams.set("targetAcademicYear", academicYear);
  destination.searchParams.set("handoff", handoffCode);
  return destination.toString();
};

export const createTcsdExternalNavigation = ({
  url = process.env.REACT_APP_TCSD_URL,
  getCurrentUrl = () => window.location.href,
  setReturnUrl = (returnUrl) => window.localStorage.setItem("td_return_url", returnUrl),
  navigate = (destination) => window.location.assign(destination),
  allowOpaqueDevelopmentHandoff = process.env.NODE_ENV !== "production",
} = {}) => ({
  handoff(handoff) {
    try {
      if (!url) return false;

      const destination = buildTcsdHandoffDestination(url, handoff, allowOpaqueDevelopmentHandoff);
      setReturnUrl(getCurrentUrl());
      navigate(destination);
      return true;
    } catch {
      return false;
    }
  },
});
